/**
 * Script Supervisor — a strictly read-only continuity assistant docked on the
 * right. Two tabs: "Ask" (concise, grounded Q&A with clickable scene citations)
 * and "Continuity" (a flagged-issues pass). It never writes or suggests script
 * content. Brand: never says "AI".
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  X, ClipboardCheck, Send, Loader2, RefreshCw, Layers, Clock, User, MapPin, Shirt,
  HelpCircle, AlertTriangle, CheckCircle2, Sparkles,
} from "lucide-react";
import { MarkdownLite } from "@/components/MarkdownLite";

const EXAMPLES = [
  "How many days pass between the first and last scene?",
  "When do we last see each main character?",
  "In 1996, would an abandoned Army tent come with setup instructions — and what would they look like?",
  "Would a character loving Seinfeld in 1988 be an anachronism?",
];

const REPORT_CATS = [
  { key: "timeline", label: "Timeline & chronology", icon: Clock },
  { key: "character", label: "Characters", icon: User },
  { key: "props_wardrobe", label: "Props & wardrobe", icon: Shirt },
  { key: "location", label: "Locations", icon: MapPin },
  { key: "unresolved", label: "Unresolved threads & plot holes", icon: HelpCircle },
];

export function SupervisorPanel({ scriptId, open, onClose, canEdit, sceneHeadings, onJumpToScene, embedded = false }) {
  const [tab, setTab] = useState("ask");
  // Ask state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [allScripts, setAllScripts] = useState([]);
  const [scopeIds, setScopeIds] = useState([]);
  const [scopeOpen, setScopeOpen] = useState(false);
  const scrollRef = useRef(null);
  // Report state
  const [reports, setReports] = useState([]);
  const [generating, setGenerating] = useState(false);
  const pollRef = useRef(null);

  const sceneMap = useMemo(() => sceneHeadings || [], [sceneHeadings]);
  const latest = reports[0] || null;

  useEffect(() => {
    if (!open) return;
    if (allScripts.length === 0) {
      api.get("/scripts").then(({ data }) => setAllScripts((data.scripts || []).filter((s) => s.id !== scriptId))).catch(() => {});
    }
  }, [open, scriptId, allScripts.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, thinking]);

  // ----- Ask -----
  const send = useCallback(async (q) => {
    const question = (q ?? input).trim();
    if (!question || thinking) return;
    const history = messages.map((m) => ({ role: m.role, text: m.text }));
    setMessages((ms) => [...ms, { role: "user", text: question }]);
    setInput("");
    setThinking(true);
    try {
      const { data } = await api.post(`/scripts/${scriptId}/supervisor/ask`, {
        question, history, scope_script_ids: scopeIds,
      });
      setMessages((ms) => [...ms, { role: "assistant", text: data.answer || "Not stated in the script." }]);
    } catch (e) {
      if (e.response?.status === 402) { toast.error("Your subscription is inactive."); }
      else toast.error(formatApiError(e.response?.data?.detail) || "The Script Supervisor couldn't answer.");
      setMessages((ms) => [...ms, { role: "assistant", text: "I couldn't check that just now — please try again." }]);
    } finally { setThinking(false); }
  }, [input, thinking, messages, scriptId, scopeIds]);

  const toggleScope = (sid) => setScopeIds((ids) => ids.includes(sid) ? ids.filter((x) => x !== sid) : [...ids, sid]);

  // ----- Continuity report -----
  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  const loadReports = useCallback(async () => {
    try {
      const { data } = await api.get(`/scripts/${scriptId}/supervisor/report`);
      const list = data.reports || [];
      setReports(list);
      if (list[0]?.status === "generating") startPoll(list[0].id);
    } catch { /* ignore */ }
  }, [scriptId]); // eslint-disable-line react-hooks/exhaustive-deps

  const startPoll = useCallback((id) => {
    stopPoll();
    setGenerating(true);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/scripts/${scriptId}/supervisor/report/${id}`);
        if (data.report.status !== "generating") {
          stopPoll(); setGenerating(false);
          setReports((rs) => { const others = rs.filter((r) => r.id !== id); return [data.report, ...others]; });
        }
      } catch { stopPoll(); setGenerating(false); }
    }, 3000);
  }, [scriptId]);

  useEffect(() => {
    if (open && tab === "report") loadReports();
    return stopPoll;
  }, [open, tab, loadReports]);

  const runReport = useCallback(async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/scripts/${scriptId}/supervisor/report`);
      setReports((rs) => [data.report, ...rs]);
      startPoll(data.report.id);
    } catch (e) {
      setGenerating(false);
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start the continuity check.");
    }
  }, [scriptId, startPoll]);

  if (!open && !embedded) return null;

  const inner = (
    <>
      {/* tabs */}
      <div className="flex border-b border-[var(--line)] shrink-0 text-sm">
        <button onClick={() => setTab("ask")} className={`flex-1 py-2.5 font-medium ${tab === "ask" ? "text-[var(--brand)] border-b-2 border-[var(--brand)]" : "text-[var(--ink-secondary)]"}`} data-testid="supervisor-tab-ask">Ask</button>
        <button onClick={() => setTab("report")} className={`flex-1 py-2.5 font-medium ${tab === "report" ? "text-[var(--brand)] border-b-2 border-[var(--brand)]" : "text-[var(--ink-secondary)]"}`} data-testid="supervisor-tab-report">Continuity check</button>
      </div>

      {tab === "ask" ? (
        <>
          {/* scope */}
          <div className="px-4 py-2 border-b border-[var(--line)] shrink-0 relative">
            <button onClick={() => setScopeOpen((o) => !o)} className="text-xs text-[var(--ink-secondary)] inline-flex items-center gap-1.5 hover:text-[var(--ink-primary)]" data-testid="supervisor-scope-btn">
              <Layers size={13} /> Reading: this script{scopeIds.length ? ` + ${scopeIds.length} more` : ""}
            </button>
            {scopeOpen && (
              <div className="absolute left-4 top-9 w-72 max-h-64 overflow-auto bg-[var(--paper)] border border-[var(--line)] rounded-lg shadow-xl p-2 z-30" data-testid="supervisor-scope-panel">
                <p className="text-[11px] uppercase tracking-widest text-[var(--ink-muted)] px-2 py-1">Also read (for sequels / episodes)</p>
                {allScripts.length === 0 ? (
                  <p className="text-sm text-[var(--ink-muted)] px-2 py-2">No other scripts.</p>
                ) : allScripts.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[var(--warm-accent)] cursor-pointer text-sm" data-testid={`supervisor-scope-${s.id}`}>
                    <input type="checkbox" checked={scopeIds.includes(s.id)} onChange={() => toggleScope(s.id)} className="accent-[var(--brand)]" />
                    <span className="truncate text-[var(--ink-primary)]">{s.title}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* messages */}
          <div ref={scrollRef} className="flex-1 overflow-auto px-4 py-3 space-y-3" data-testid="supervisor-messages">
            {messages.length === 0 && !thinking && (
              <div className="text-sm text-[var(--ink-secondary)]" data-testid="supervisor-empty">
                <p className="mb-3">Ask me about your script — continuity, chronology, who did what and when (I'll cite the scenes) — or check a real-world fact for period accuracy. I don't write or suggest story; that's your job.</p>
                <p className="text-[11px] uppercase tracking-widest text-[var(--ink-muted)] mb-1.5">Try asking</p>
                <div className="space-y-1.5">
                  {EXAMPLES.map((ex, i) => (
                    <button key={i} onClick={() => send(ex)} className="block w-full text-left px-3 py-2 rounded-lg border border-[var(--line)] hover:border-[var(--brand)]/50 hover:bg-[var(--warm-accent)] text-[var(--ink-primary)]" data-testid={`supervisor-example-${i}`}>“{ex}”</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"} data-testid={`supervisor-msg-${m.role}-${i}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${m.role === "user" ? "bg-[var(--brand)] text-white rounded-br-sm" : "bg-[var(--warm-accent)] text-[var(--ink-primary)] rounded-bl-sm"}`}>
                  {m.role === "assistant" ? <MarkdownLite text={m.text} sceneMap={sceneMap} onJump={onJumpToScene} /> : m.text}
                </div>
              </div>
            ))}
            {thinking && (
              <div className="flex justify-start" data-testid="supervisor-thinking">
                <div className="bg-[var(--warm-accent)] rounded-2xl rounded-bl-sm px-3.5 py-2.5"><Loader2 size={15} className="animate-spin text-[var(--ink-secondary)]" /></div>
              </div>
            )}
          </div>

          {/* input */}
          <div className="border-t border-[var(--line)] p-3 shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={1} placeholder="Ask about your script…"
                className="flex-1 resize-none max-h-28 bg-[var(--canvas)] border border-[var(--line)] rounded-xl px-3 py-2 text-sm outline-none focus:border-[var(--brand)]"
                data-testid="supervisor-input"
              />
              <button onClick={() => send()} disabled={thinking || !input.trim()} className="btn-brand p-2.5 rounded-xl disabled:opacity-40" data-testid="supervisor-send"><Send size={16} /></button>
            </div>
          </div>
        </>
      ) : (
        /* ---------- Continuity report tab ---------- */
        <div className="flex-1 overflow-auto p-4" data-testid="supervisor-report">
          <div className="flex items-center justify-between mb-3 gap-2">
            <p className="text-sm text-[var(--ink-secondary)]">A continuity pass over this script — flagged issues only, with scene references. It won't suggest fixes.</p>
          </div>

          {latest?.stale && latest.status === "ready" && (
            <div className="flex items-center gap-2 text-xs text-[var(--warn)] bg-[var(--warn)]/10 rounded-lg px-3 py-2 mb-3" data-testid="supervisor-report-stale">
              <AlertTriangle size={13} /> Your draft changed since this check. Re-run for current results.
            </div>
          )}

          {canEdit && (
            <button onClick={runReport} disabled={generating} className="btn-brand w-full inline-flex items-center justify-center gap-2 mb-4 disabled:opacity-50" data-testid="supervisor-run-report">
              {generating ? <><Loader2 size={15} className="animate-spin" /> Reading your script…</> : <><RefreshCw size={15} /> {latest ? "Re-run continuity check" : "Run continuity check"}</>}
            </button>
          )}

          {generating && !latest && (
            <p className="text-sm text-[var(--ink-muted)] text-center py-8" data-testid="supervisor-report-generating">Working through every scene… this takes ~30-60 seconds.</p>
          )}

          {latest?.status === "error" && (
            <p className="text-sm text-[var(--err)]" data-testid="supervisor-report-error">{latest.error}</p>
          )}

          {latest?.status === "ready" && (
            <div data-testid="supervisor-report-result">
              {latest.total_issues === 0 ? (
                <div className="text-center py-8 text-[var(--ink-secondary)]">
                  <CheckCircle2 size={32} className="mx-auto text-[var(--ok)] mb-2" />
                  <p className="font-display text-lg text-[var(--ink-primary)]">No continuity issues flagged</p>
                  <p className="text-sm">Nothing jumped out across your scenes.</p>
                </div>
              ) : (
                <div className="space-y-5">
                  <p className="text-xs text-[var(--ink-muted)]">{latest.total_issues} item{latest.total_issues > 1 ? "s" : ""} flagged across {latest.page_estimate} page{latest.page_estimate > 1 ? "s" : ""}.</p>
                  {REPORT_CATS.map(({ key, label, icon: Icon }) => {
                    const items = latest.issues?.[key] || [];
                    if (!items.length) return null;
                    return (
                      <div key={key} data-testid={`supervisor-cat-${key}`}>
                        <p className="flex items-center gap-2 text-[var(--ink-primary)] font-semibold text-sm mb-2"><Icon size={15} className="text-[var(--brand)]" /> {label} <span className="text-[var(--ink-muted)] font-normal">({items.length})</span></p>
                        <ul className="space-y-2">
                          {items.map((it, i) => (
                            <li key={i} className="text-sm text-[var(--ink-secondary)] border-l-2 border-[var(--line)] pl-3" data-testid={`supervisor-issue-${key}-${i}`}>
                              <MarkdownLite text={it.note} sceneMap={sceneMap} onJump={onJumpToScene} />
                              {(it.scenes || []).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {it.scenes.map((n) => {
                                    const target = sceneMap[n - 1];
                                    return (
                                      <button key={n} onClick={() => target && onJumpToScene(target.id)} className="text-[11px] bg-[var(--warm-accent)] text-[var(--brand)] px-2 py-0.5 rounded-full hover:bg-[var(--brand)] hover:text-white transition-colors" data-testid={`supervisor-issue-scene-${n}`}>Scene {n}</button>
                                    );
                                  })}
                                </div>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {!latest && !generating && (
            <div className="text-center py-10 text-[var(--ink-secondary)]" data-testid="supervisor-report-empty">
              <Sparkles size={28} className="mx-auto text-[var(--brand)] mb-2" />
              <p className="text-sm">Run a check to flag timeline slips, character inconsistencies, prop/location continuity, and unresolved threads.</p>
            </div>
          )}
        </div>
      )}
    </>
  );

  if (embedded) {
    return <div className="flex flex-col h-full min-h-0" data-testid="supervisor-panel">{inner}</div>;
  }

  return (
    <div className="fixed inset-0 z-40 w-full sm:relative sm:inset-auto sm:w-[420px] sm:shrink-0 sm:h-full bg-[var(--paper)] border-l border-[var(--line)] shadow-[-4px_0_24px_rgba(0,0,0,0.10)] sm:shadow-none flex flex-col" data-testid="supervisor-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--line)] shrink-0">
        <div className="min-w-0">
          <span className="flex items-center gap-2 font-display text-lg text-[var(--ink-primary)]"><ClipboardCheck size={17} className="text-[var(--brand)]" /> Script Supervisor</span>
          <p className="text-[11px] text-[var(--ink-muted)] mt-0.5">Answers questions about your script and checks real-world facts — never writes your story.</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]" data-testid="supervisor-close"><X size={17} /></button>
      </div>
      {inner}
    </div>
  );
}
